import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BrandLogoLockup from './BrandLogoLockup';
import { firebaseApp, isFirebaseConfigured } from '../firebase/config';
import { attemptAdminLogin, DEMO_EMAIL, DEMO_PASSWORD } from '../auth/attemptAdminLogin';

/** Same string for footer text, embed, and “Open in Google Maps” (edit once to move pin). */
const OFFICE_MAP_ADDRESS =
  'Babail Naka, Sanoli Road, Huda Bypass, Panipat, Haryana 132103, India';

const GOOGLE_MAPS_EMBED_URL = `https://maps.google.com/maps?q=${encodeURIComponent(
  OFFICE_MAP_ADDRESS
)}&z=16&ie=UTF8&iwloc=&output=embed`;

const GOOGLE_MAPS_EXTERNAL_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  OFFICE_MAP_ADDRESS
)}`;

const Footer = () => {
  const navigate = useNavigate();
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const demoMode = !isFirebaseConfigured() || !firebaseApp;
  const [credentials, setCredentials] = useState(() => ({
    email: demoMode ? DEMO_EMAIL : '',
    password: demoMode ? DEMO_PASSWORD : '',
  }));
  const [adminError, setAdminError] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);

  const handleAdminClick = () => {
    setShowAdminLogin(true);
    setAdminError('');
    const nextDemo = !isFirebaseConfigured() || !firebaseApp;
    setCredentials({
      email: nextDemo ? DEMO_EMAIL : '',
      password: nextDemo ? DEMO_PASSWORD : '',
    });
  };

  const handleCloseAdminLogin = () => {
    setShowAdminLogin(false);
    setAdminError('');
  };

  const handleAdminFieldChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    setAdminError('');
  };

  const handleAdminModalSubmit = async (e) => {
    e.preventDefault();
    setAdminLoading(true);
    setAdminError('');
    const result = await attemptAdminLogin(credentials.email, credentials.password);
    setAdminLoading(false);
    if (result.ok) {
      setShowAdminLogin(false);
      navigate('/dashboard', { replace: true });
    } else {
      setAdminError(result.error);
    }
  };

  const footerLinks = {
    explore: [
      { name: 'Design Philosophy', href: '#' },
      { name: 'Studio', href: '#' },
      { name: 'Portfolio', href: '#' }
    ],
    legal: [
      { name: 'Privacy', href: '#' },
      { name: 'Terms', href: '#' }
    ],
    connect: [
      { name: 'Instagram', href: '#' },
      { name: 'Pinterest', href: '#' }
    ]
  };

  return (
    <>
      <footer className="w-full rounded-t-[2rem] sm:rounded-t-[3rem] mt-16 sm:mt-20 md:mt-24 bg-gray-800">
        <div className="max-w-screen-2xl mx-auto p-8 sm:p-12 md:p-16 lg:p-24 pb-10 sm:pb-12">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-10 lg:gap-12 xl:gap-16">
            {/* Left: brand + link columns */}
            <div className="flex flex-col md:flex-row flex-1 min-w-0 justify-between items-start gap-8 sm:gap-12 w-full">
          {/* Brand Section — logo top-left of footer */}
          <div className="max-w-sm w-full md:w-auto flex flex-col items-start text-left">
            <Link to="/" className="relative z-10 mb-4 sm:mb-6 inline-block" aria-label="Jatin Interiors — Home">
              <BrandLogoLockup variant="footer" />
            </Link>
            <div className="text-gray-300 text-sm leading-relaxed mb-4 sm:mb-6 md:mb-8 space-y-2">
              <div className="flex items-start gap-2">
                <span className="material-symbols-outlined text-sm mt-0.5" style={{color: '#C8A96A'}}>
                  location_on
                </span>
                <span>{OFFICE_MAP_ADDRESS}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="material-symbols-outlined text-sm mt-0.5" style={{color: '#C8A96A'}}>
                  email
                </span>
                <span>Jatininteriorspanipat@gmail.com</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="material-symbols-outlined text-sm mt-0.5" style={{color: '#C8A96A'}}>
                  language
                </span>
                <span>www.Jatininteriors.in</span>
              </div>
            </div>
            <div className="flex gap-4 sm:gap-6">
              <span className="material-symbols-outlined text-white cursor-pointer hover:text-primary transition-colors text-xl sm:text-2xl">
                share
              </span>
              <span className="material-symbols-outlined text-white cursor-pointer hover:text-primary transition-colors text-xl sm:text-2xl">
                favorite
              </span>
              <span className="material-symbols-outlined text-white cursor-pointer hover:text-primary transition-colors text-xl sm:text-2xl">
                location_on
              </span>
            </div>
          </div>

          {/* Links Section */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 md:gap-16 w-full md:w-auto">
            {/* Explore */}
            <div>
              <h6 className="font-manrope text-xs sm:text-sm uppercase tracking-widest text-white font-bold mb-3 sm:mb-4 md:mb-6">
                Explore
              </h6>
              <ul className="space-y-2 sm:space-y-3 md:space-y-4 text-gray-300 text-xs sm:text-sm uppercase tracking-widest">
                {footerLinks.explore.map((link) => (
                  <li key={link.name}>
                    <a 
                      className="hover:text-white transition-colors" 
                      href={link.href}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
              {/* Admin Panel Button under Portfolio */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleAdminClick}
                  className="flex items-center gap-2 px-3 py-1 rounded-full border border-gray-600 text-white font-bold hover:border-primary hover:text-primary transition-colors text-xs sm:text-sm"
                  title="Admin Panel"
                >
                  <span className="material-symbols-outlined text-sm">admin_panel_settings</span>
                  <span>Admin Panel</span>
                </button>
              </div>
            </div>

            {/* Legal */}
            <div>
              <h6 className="font-manrope text-xs sm:text-sm uppercase tracking-widest text-white font-bold mb-3 sm:mb-4 md:mb-6">
                Legal
              </h6>
              <ul className="space-y-2 sm:space-y-3 md:space-y-4 text-gray-300 text-xs sm:text-sm uppercase tracking-widest">
                {footerLinks.legal.map((link) => (
                  <li key={link.name}>
                    <a 
                      className="hover:text-white transition-colors" 
                      href={link.href}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h6 className="font-manrope text-xs sm:text-sm uppercase tracking-widest text-white font-bold mb-3 sm:mb-4 md:mb-6">
                Connect
              </h6>
              <ul className="space-y-2 sm:space-y-3 md:space-y-4 text-gray-300 text-xs sm:text-sm uppercase tracking-widest">
                {footerLinks.connect.map((link) => (
                  <li key={link.name}>
                    <a 
                      className="hover:text-white transition-colors" 
                      href={link.href}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
            </div>

            {/* Right: compact map box */}
            <aside className="w-full max-w-md mx-auto lg:mx-0 lg:max-w-none lg:w-64 xl:w-72 shrink-0">
              <div className="rounded-xl border border-gray-600 bg-gray-900/90 p-3 sm:p-4 shadow-xl ring-1 ring-white/5">
                <h6 className="font-manrope text-[10px] sm:text-xs uppercase tracking-widest text-white font-bold mb-2">
                  Find us
                </h6>
                <div className="relative w-full overflow-hidden rounded-lg border border-gray-700 bg-gray-950 h-[140px] sm:h-[160px] xl:h-[170px]">
                  <iframe
                    title="Jatin Interiors — Google Maps"
                    src={GOOGLE_MAPS_EMBED_URL}
                    className="absolute inset-0 h-full w-full border-0"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <a
                  href={GOOGLE_MAPS_EXTERNAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#C8A96A] hover:text-amber-300 transition-colors"
                >
                  Open in Google Maps
                  <span className="material-symbols-outlined text-sm">open_in_new</span>
                </a>
              </div>
            </aside>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="px-4 sm:px-8 md:px-16 lg:px-24 pb-6 sm:pb-8 md:pb-12">
          <div className="border-t border-gray-700 pt-4 sm:pt-6 md:pt-8 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 text-gray-400 font-manrope text-xs sm:text-sm uppercase tracking-widest text-center">
            <span>© 2024 JATIN INTERIORS. Elevated Living.</span>
          </div>
          <p className="mt-3 text-center text-sm sm:text-base tracking-wide text-gray-300">
            Developed by{' '}
            <a
              href="https://www.larawansdigital.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#C8A96A] hover:text-amber-300 transition-colors"
            >
              Larawans Digital, Kaithal Haryana
            </a>
          </p>
        </div>
      </footer>

      {/* Admin Login Modal */}
      {showAdminLogin && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          role="presentation"
          onClick={(ev) => {
            if (ev.target === ev.currentTarget) handleCloseAdminLogin();
          }}
        >
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            {/* Close Button */}
            <button
              onClick={handleCloseAdminLogin}
              className="absolute top-4 right-4 p-2 rounded-full bg-stone-100 hover:bg-stone-200 transition-colors"
            >
              <span className="material-symbols-outlined text-stone-600">close</span>
            </button>

            {/* Admin Login Content */}
            <div className="p-8">
              {/* Logo and Title */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{backgroundColor: '#C8A96A'}}>
                  <span className="material-symbols-outlined text-white text-2xl">admin_panel_settings</span>
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{color: '#3E3E3E'}}>Admin Login</h2>
                <p className="text-stone-600">Jatin Interiors Management System</p>
              </div>

              <form onSubmit={handleAdminModalSubmit} className="space-y-4">
                <div>
                  <label htmlFor="footer-admin-email" className="block text-sm font-medium mb-2" style={{ color: '#3E3E3E' }}>
                    Email
                  </label>
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400">
                      email
                    </span>
                    <input
                      id="footer-admin-email"
                      name="email"
                      type="email"
                      value={credentials.email}
                      onChange={handleAdminFieldChange}
                      className="w-full pl-10 pr-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors text-stone-900"
                      placeholder="Enter email"
                      autoComplete="username"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="footer-admin-password" className="block text-sm font-medium mb-2" style={{ color: '#3E3E3E' }}>
                    Password
                  </label>
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400">
                      lock
                    </span>
                    <input
                      id="footer-admin-password"
                      name="password"
                      type="password"
                      value={credentials.password}
                      onChange={handleAdminFieldChange}
                      className="w-full pl-10 pr-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors text-stone-900"
                      placeholder="Enter password"
                      autoComplete="current-password"
                      required
                    />
                  </div>
                </div>

                {adminError && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200" role="alert">
                    <p className="text-sm font-medium text-red-700">{adminError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={adminLoading}
                  className="w-full py-3 rounded-lg font-medium transition-all duration-300 text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#C8A96A' }}
                >
                  {adminLoading ? 'Signing in…' : 'Sign In'}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link
                  to="/admin/login"
                  className="text-xs font-medium text-stone-600 hover:text-[#C8A96A] underline"
                  onClick={handleCloseAdminLogin}
                >
                  Open full login page
                </Link>
              </div>

              <div className="mt-6 p-4 rounded-lg bg-stone-50 border border-stone-200">
                <p className="text-sm font-medium mb-2" style={{ color: '#3E3E3E' }}>
                  {isFirebaseConfigured() && firebaseApp ? 'Sign-in' : 'Demo login'}
                </p>
                <p className="text-xs text-stone-600 leading-relaxed">
                  {isFirebaseConfigured() && firebaseApp ? (
                    <>
                      Firebase account, or demo: <span className="font-mono">{DEMO_EMAIL}</span> /{' '}
                      <span className="font-mono">{DEMO_PASSWORD}</span>
                    </>
                  ) : (
                    <>
                      <span className="font-mono">{DEMO_EMAIL}</span> / <span className="font-mono">{DEMO_PASSWORD}</span>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
